/**
 * Domain Layer - CsvFile ValueObject
 * CSVファイルの値オブジェクト定義
 */

/**
 * CSVファイルを表す値オブジェクト
 */
export interface CsvFile {
  /** ファイル名 */
  readonly name: string;
  /** ファイルサイズ（バイト） */
  readonly size: number;
  /** MIMEタイプ */
  readonly mimeType: string;
  /** 最終更新日時 */
  readonly lastModified: number;
}

/**
 * 許可されるCSVファイルのMIMEタイプ
 */
const VALID_CSV_MIME_TYPES = [
  'text/csv',
  'application/csv',
  'text/plain',
  'application/vnd.ms-excel',
] as const;

/**
 * 許可されるCSVファイルの拡張子
 */
const VALID_CSV_EXTENSIONS = ['.csv', '.CSV'] as const;

/**
 * 最大ファイルサイズ（10MB）
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * ブラウザのFileオブジェクトからCsvFileを生成する
 * @param file ブラウザのFileオブジェクト
 * @returns CsvFile値オブジェクト
 */
export const createCsvFile = (file: File): CsvFile => ({
  name: file.name,
  size: file.size,
  mimeType: file.type,
  lastModified: file.lastModified,
});

/**
 * CSVファイルとして有効かどうかを判定する
 * @param csvFile CsvFile値オブジェクト
 * @returns 有効な場合true
 */
export const isValidCsvFile = (csvFile: CsvFile): boolean => {
  // ファイル名の拡張子チェック
  const hasValidExtension = VALID_CSV_EXTENSIONS.some((ext) =>
    csvFile.name.endsWith(ext)
  );

  // MIMEタイプチェック（空の場合は拡張子のみで判定）
  const hasValidMimeType =
    csvFile.mimeType === '' ||
    VALID_CSV_MIME_TYPES.includes(csvFile.mimeType as typeof VALID_CSV_MIME_TYPES[number]);

  // ファイルサイズチェック
  const hasValidSize = csvFile.size > 0 && csvFile.size <= MAX_FILE_SIZE;

  return hasValidExtension && hasValidMimeType && hasValidSize;
};

/**
 * ファイルサイズを人間が読みやすい形式に変換する
 * @param bytes バイト数
 * @returns フォーマットされた文字列（例: "1.5 MB"）
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
